import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Video, MessageCircle, CreditCard, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import ComplianceBadge from "../components/ComplianceBadge";
import { useI18n } from "../lib/i18n";
import { api, formatError } from "../lib/api";
import { useSettings, buildWhatsAppLink } from "../lib/settings";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";

export default function Appointments() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const settings = useSettings();
  const [items, setItems] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [doctorId, setDoctorId] = useState("");
  const [mode, setMode] = useState("in_person");
  const [reason, setReason] = useState("");
  const [slots, setSlots] = useState([]);
  const [chosenSlot, setChosenSlot] = useState("");
  const [confirm, setConfirm] = useState(null);

  const load = () => api.get("/appointments").then(({ data }) => setItems(data));

  useEffect(() => {
    load();
    api.get("/doctors").then(({ data }) => {
      setDoctors(data);
      if (data.length === 1) setDoctorId(data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!doctorId || !date) { setSlots([]); return; }
    api.get(`/availability/${doctorId}/slots`, { params: { date } })
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]));
  }, [doctorId, date]);

  const submit = async () => {
    if (!doctorId || !date || !chosenSlot || !reason) {
      toast.error(t("error_generic"));
      return;
    }
    try {
      const { data } = await api.post("/appointments", {
        doctor_id: doctorId, date, time: chosenSlot, reason, mode, language: lang,
      });
      toast.success(t("saved"));
      setConfirm(data);
      setOpen(false);
      setDate(""); setChosenSlot(""); setReason(""); setMode("in_person");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/appointments/${id}`, { status }); toast.success(t("saved")); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const updatePayment = async (id, payment_status) => {
    try { await api.patch(`/appointments/${id}/payment`, { payment_status }); toast.success(t("saved")); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const payWithStripe = async (apptId) => {
    try {
      const { data } = await api.post("/payments/checkout", {
        appointment_id: apptId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) { toast.error(formatError(err)); }
  };

  const buildWaText = (a) => {
    const langWord = { es: "Consulta", fr: "Rendez-vous", ht: "Randevou" }[lang] || "Consulta";
    return `${langWord} - Dr. Dimy Laguerre\n` +
           `${t("patient")}: ${a.patient_name}\n` +
           `${t("date")}: ${a.date} ${a.time}\n` +
           `${t("mode")}: ${t(a.mode)}\n` +
           `${t("reason")}: ${a.reason}\n` +
           `${t("payment")}: ${t(`payment_${a.payment_status}`)} (20€)`;
  };

  const dayLabel = (d) => new Date(d + "T00:00").toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="space-y-6 fade-up" data-testid="appointments-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl">{t("appointments")}</h1>
        {user?.role === "patient" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="new-appointment-btn">
                <Plus className="w-4 h-4 mr-2" /> {t("new_appointment")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{t("new_appointment")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {doctors.length > 1 && (
                  <div>
                    <Label>{t("doctor")}</Label>
                    <Select value={doctorId} onValueChange={setDoctorId}>
                      <SelectTrigger data-testid="appt-doctor"><SelectValue placeholder={t("select_doctor")} /></SelectTrigger>
                      <SelectContent>
                        {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} — {d.specialty}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>{t("mode")}</Label>
                  <Select value={mode} onValueChange={setMode}>
                    <SelectTrigger data-testid="appt-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_person">{t("in_person")}</SelectItem>
                      <SelectItem value="video">{t("video")}</SelectItem>
                      <SelectItem value="phone">Llamada telefónica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("select_date")}</Label>
                  <Input type="date" value={date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => { setDate(e.target.value); setChosenSlot(""); }} data-testid="appt-date" />
                </div>
                {date && (
                  <div>
                    <Label>{t("available_slots")}</Label>
                    {slots.length === 0 ? (
                      <div className="text-sm text-muted-foreground p-3 bg-[#F3F1EC] rounded-lg">{t("no_slots")}</div>
                    ) : (
                      <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto p-1" data-testid="slots-grid">
                        {slots.map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setChosenSlot(s)}
                            className={`px-3 py-2 rounded-md text-sm border transition-colors ${
                              chosenSlot === s ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-[#F3F1EC] border-border"
                            }`}
                            data-testid={`slot-${s}`}
                          >{s}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <Label>{t("reason")}</Label>
                  <Textarea required value={reason} onChange={(e) => setReason(e.target.value)} data-testid="appt-reason" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} disabled={!chosenSlot || !reason} className="bg-primary hover:bg-primary/90 w-full" data-testid="appt-save-btn">
                  {t("confirm")} — {settings.consultation_price_eur || 20}€
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Confirmation modal */}
      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("booking_confirmed")}</DialogTitle></DialogHeader>
          {confirm && (
            <div className="space-y-3" data-testid="booking-confirm">
              <p className="text-sm text-muted-foreground">{t("booking_confirmed_sub")}</p>
              <div className="bg-[#F3F1EC] rounded-lg p-3 text-sm">
                <div><strong>{t("date")}:</strong> {confirm.date} {confirm.time}</div>
                <div><strong>{t("mode")}:</strong> {t(confirm.mode)}</div>
                <div><strong>{t("reason")}:</strong> {confirm.reason}</div>
              </div>
              <a
                href={buildWhatsAppLink(settings.whatsapp_number, buildWaText(confirm))}
                target="_blank" rel="noreferrer"
                className="block"
                data-testid="confirm-whatsapp-btn"
              >
                <Button className="w-full bg-[#25D366] hover:bg-[#20BD5C] text-white">
                  <MessageCircle className="w-4 h-4 mr-2" /> {t("notify_whatsapp")}
                </Button>
              </a>
              <Button className="w-full bg-[#635BFF] hover:bg-[#5249f5] text-white" onClick={() => payWithStripe(confirm.id)} data-testid="confirm-stripe-btn">
                <CreditCard className="w-4 h-4 mr-2" /> Pagar {confirm.price_eur || settings.consultation_price_eur || 20}€ con tarjeta (Stripe)
              </Button>
              <a href={settings.wise_pay_link} target="_blank" rel="noreferrer" className="block" data-testid="confirm-wise-btn">
                <Button variant="outline" className="w-full">
                  <CreditCard className="w-4 h-4 mr-2" /> Pagar {confirm.price_eur || settings.consultation_price_eur || 20}€ con Wise
                </Button>
              </a>
              <div className="pt-2">
                <ComplianceBadge variant="inline" />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader><CardTitle className="font-display">{t("upcoming")}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {items.length === 0 && <div className="text-sm text-muted-foreground">{t("no_data")}</div>}
          {items.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 py-3 border-b last:border-0" data-testid={`appt-${a.id}`}>
              <div>
                <div className="text-sm font-medium">{dayLabel(a.date)} · {a.time}</div>
                <div className="text-xs text-muted-foreground">
                  {user?.role === "patient" ? a.doctor_name : a.patient_name} · {a.reason}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline">{t(a.mode)}</Badge>
                <Badge>{t(a.status)}</Badge>
                <Badge variant={a.payment_status === "paid" ? "default" : "outline"}
                       className={a.payment_status === "paid" ? "bg-[#4A6E4F] hover:bg-[#4A6E4F]" : ""}>
                  {t(`payment_${a.payment_status || "pending"}`)}
                </Badge>
                {a.mode === "video" && a.video_link && a.status === "scheduled" && (
                  <Link to={`/app/video/${a.id}?appt=${a.id}&pid=${a.patient_id}`}>
                    <Button size="sm" variant="outline" data-testid={`join-video-${a.id}`}><Video className="w-4 h-4 mr-1" /> {t("join_video")}</Button>
                  </Link>
                )}
                {user?.role === "patient" && a.payment_status === "pending" && (
                  <Button size="sm" className="bg-[#635BFF] hover:bg-[#5249f5] text-white" onClick={() => payWithStripe(a.id)} data-testid={`pay-stripe-${a.id}`}>
                    <CreditCard className="w-4 h-4 mr-1" /> Pagar {settings.consultation_price_eur || 20}€ (Tarjeta)
                  </Button>
                )}
                {user?.role === "patient" && a.payment_status === "pending" && settings.wise_pay_link && (
                  <a href={settings.wise_pay_link} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" data-testid={`pay-${a.id}`}><CreditCard className="w-4 h-4 mr-1" /> Wise</Button>
                  </a>
                )}
                {user?.role === "patient" && settings.whatsapp_number && (
                  <a href={buildWhatsAppLink(settings.whatsapp_number, buildWaText(a))} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="text-[#25D366] border-[#25D366]" data-testid={`wa-${a.id}`}>
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                  </a>
                )}
                {user?.role === "doctor" && (
                  <>
                    {a.payment_status !== "paid" && (
                      <Button size="sm" variant="outline" onClick={() => updatePayment(a.id, "paid")} data-testid={`mark-paid-${a.id}`}>
                        <Check className="w-4 h-4 mr-1" /> {t("mark_paid")}
                      </Button>
                    )}
                    {a.status === "scheduled" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, "completed")} data-testid={`complete-${a.id}`}>{t("completed")}</Button>
                        <Button size="sm" variant="ghost" onClick={() => updateStatus(a.id, "cancelled")} data-testid={`cancel-${a.id}`}>{t("cancel")}</Button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
